import { useEffect, useState } from 'react';
import { listBranches, listWarehouses } from '../../api/orgData';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { DashboardSkeleton, Drawer, EmptyState, ErrorNote, KPICard, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconArchive, IconBuilding, IconLayers, IconShield, IconTruck } from '../../components/icons';
import type { Branch, Warehouse } from '../../types/domain';

interface ConfigItem {
  key: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  summary: string;
  detail: string;
}

const CONFIG: ConfigItem[] = [
  {
    key: 'security',
    label: 'Keamanan & RBAC',
    icon: IconShield,
    summary: 'RLS + RBAC catalog: locked',
    detail: 'Row-Level Security aktif di seluruh tabel operasional dengan isolasi per-tenant dan per-cabang. Katalog roles/permissions/role_permissions terkunci dari akses langsung anon/authenticated (migrasi 0037).',
  },
  {
    key: 'integration',
    label: 'Integrasi',
    icon: IconLayers,
    summary: 'Supabase Auth · Postgres · PostgREST',
    detail: 'Autentikasi, database, dan API data aktif dan terhubung untuk tenant ini.',
  },
];

/**
 * System Admin workspace — tenant-scoped system administration, distinct
 * from Platform Owner (which is SaaS/platform-level). SYSTEM_ADMIN
 * administers THIS tenant's branches/warehouses and reviews system
 * activity, not cross-tenant billing/analytics — that scope stays with
 * PLATFORM_OWNER only. Branch/warehouse counts are real (org reference
 * data, RLS tenant-scoped, no permission gate per orgData.ts); the
 * activity feed reuses the same real audit_events table every other
 * read-only panel in this app uses.
 */
export default function SystemAdminDashboard() {
  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const open = CONFIG.find((c) => c.key === openKey) ?? null;

  useEffect(() => {
    let cancelled = false;
    Promise.all([listBranches(), listWarehouses(), listAuditEvents(15)])
      .then(([b, w, ev]) => {
        if (cancelled) return;
        setBranches(b);
        setWarehouses(w);
        setEvents(ev);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        <KPICard label="Cabang Aktif" value={branches?.length ?? 0} icon={<IconBuilding size={16} />} tone="primary" />
        <KPICard label="Gudang Aktif" value={warehouses?.length ?? 0} icon={<IconTruck size={16} />} tone="info" />
      </div>

      <div className="alex-grid-8-4">
        <SectionCard title="Aktivitas Sistem Terbaru">
          {!events || events.length === 0 ? (
            <EmptyState text="Belum ada aktivitas tercatat." variant="table" />
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Waktu</th>
                  <th style={thStyle}>Kejadian</th>
                  <th style={thStyle}>Aksi</th>
                  <th style={thStyle}>Referensi</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 10).map((e) => (
                  <tr key={e.event_id}>
                    <td style={tdStyle}>{new Date(e.occurred_at).toLocaleString('id-ID')}</td>
                    <td style={tdStyle}>{describeActivity(e)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={e.action} />
                    </td>
                    <td style={tdStyle}>{e.reference ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Cabang & Gudang" action="/app/suppliers">
            {!branches || branches.length === 0 ? (
              <EmptyState text="Belum ada cabang tercatat." variant="compact" />
            ) : (
              <table style={tableStyle}>
                <tbody>
                  {branches.slice(0, 6).map((b) => (
                    <tr key={b.branch_id}>
                      <td style={tdStyle}>{b.name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <StatusBadge status={b.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <SectionCard title="Konfigurasi Sistem">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {CONFIG.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setOpenKey(c.key)}
                  className="alex-row-clickable"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: 'none', background: 'none', padding: '8px 6px', borderRadius: 'var(--alex-control-radius)', width: '100%', textAlign: 'left' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <c.icon size={16} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--alex-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.summary}</div>
                    </div>
                  </div>
                  <IconArchive size={14} />
                </button>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      <Drawer open={!!open} onClose={() => setOpenKey(null)} title={open?.label ?? ''}>
        {open && <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', lineHeight: 1.55, margin: 0 }}>{open.detail}</p>}
      </Drawer>
    </div>
  );
}
